import { Button, Dialog, Eyebrow, Input, Label } from "@pej/kore/components";
import { useCreateWorkspace } from "@/api/workspaces";
import { toastMutationError } from "@/components/common/toast-mutation-error";
import { useForm } from "@/lib/forms";

interface CreateWorkspaceDialogProps {
    organization: { id: string; name: string } | null;
    onOpenChange: (open: boolean) => void;
    onCreated: (workspaceId: string) => void;
}

type FormValues = {
    name: string;
};

export function CreateWorkspaceDialog({ organization, onOpenChange, onCreated }: CreateWorkspaceDialogProps) {
    const mutation = useCreateWorkspace();

    const form = useForm<FormValues>({
        defaultValues: { name: "" },
    });

    const { name } = form.watch();
    const isValid = name.trim().length > 0;

    async function onSubmit(values: FormValues) {
        if (!organization) return;
        try {
            const created = await mutation.mutateAsync({ orgId: organization.id, name: values.name.trim() });
            onCreated(created.id);
            // Reset state after a successful create so the next open starts clean.
            form.reset();
        } catch (err) {
            toastMutationError("Create failed", err);
        }
    }

    return (
        <Dialog
            open={organization != null}
            onOpenChange={(open) => {
                if (!open && mutation.isPending) return;
                onOpenChange(open);
            }}
        >
            <Dialog.Content className="max-w-md">
                <Dialog.Header>
                    {organization && (
                        <Eyebrow as="p" className="mb-1">
                            Create workspace in {organization.name}
                        </Eyebrow>
                    )}
                    <Dialog.Title>New workspace</Dialog.Title>
                    <Dialog.Description>
                        Workspaces own their locations, screens and templates. You'll be the workspace admin.
                    </Dialog.Description>
                </Dialog.Header>

                <form
                    className="flex flex-col gap-3"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void form.handleSubmit(onSubmit)(e);
                    }}
                >
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="ws-name">Display name</Label>
                        <Input id="ws-name" placeholder="e.g. Downtown Bakery" {...form.register("name")} />
                    </div>

                    <Dialog.Footer>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={mutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!isValid || mutation.isPending}>
                            {mutation.isPending ? "Creating…" : "Create workspace"}
                        </Button>
                    </Dialog.Footer>
                </form>
            </Dialog.Content>
        </Dialog>
    );
}
